import { useEffect, useState } from 'react';

// Mirrors the class-toggling logic in SideNav.tsx's applyTheme(): the user's
// explicit choice is an 'html.dark-mode' / 'html.light-mode' class; with
// neither present ('auto'), the OS-level prefers-color-scheme wins.
function computeIsDarkMode(): boolean {
  if (typeof document === 'undefined') return false;
  const classList = document.documentElement.classList;
  if (classList.contains('dark-mode')) return true;
  if (classList.contains('light-mode')) return false;
  return globalThis.window !== undefined
    && globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches === true;
}

/**
 * Tracks whether the app is currently in dark mode, reacting to both the
 * user's manual light/dark/auto toggle (SideNav) and OS-level scheme changes
 * (relevant while the toggle is set to 'auto'). Used to keep MUI's theme
 * palette in sync with the CSS custom-property theme applied elsewhere.
 */
export function useIsDarkMode(): boolean {
  const [isDark, setIsDark] = useState(computeIsDarkMode);

  useEffect(() => {
    const update = () => setIsDark(computeIsDarkMode());

    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    const media = globalThis.matchMedia?.('(prefers-color-scheme: dark)');
    media?.addEventListener('change', update);

    update();

    return () => {
      observer.disconnect();
      media?.removeEventListener('change', update);
    };
  }, []);

  return isDark;
}
